/* ============================================================================
   CREAR FACTURA · reglas de presentación

   NO HAY NINGUNA REGLA DE NEGOCIO NUEVA AQUÍ. Las condiciones de habilitado se
   copian TAL CUAL de donde ya vivían -- `TotalesFactura.js` y
   `ConfirmBilling.js` -- y se extraen a un solo sitio porque el rediseño pone
   el mismo botón "Emitir factura" en DOS lugares (la barra superior y el
   resumen del pie), y dos copias de la misma condición divergen tarde o
   temprano.

   Si alguna de estas funciones se comportara distinto a como se comportaba en
   su sitio original, sería un error, no una mejora: las validaciones de
   facturación están fuera del alcance de este rediseño.
   ========================================================================== */

/**
 * ¿Se puede abrir el modal de emisión?
 *
 * Copiado de TotalesFactura.js:
 *     disabled={total <= 0 && !tienePlan}
 *
 * Se expresa en positivo (puede / no puede) en vez de en negativo, que es lo
 * único que cambia.
 */
export function puedeEmitir({ total, tienePlan }) {
    return !(Number(total) <= 0 && !tienePlan);
}

/**
 * Por qué NO se puede, para poder decirlo. Hoy el botón simplemente se apaga
 * sin explicación; el motivo se deriva de la MISMA condición, no de una nueva.
 * Devuelve null cuando sí se puede.
 */
export function motivoNoEmitir({ total, tienePlan, procedimientos = [] }) {
    if (puedeEmitir({ total, tienePlan })) return null;
    if (procedimientos.length === 0) return "Agrega al menos un procedimiento";
    return "El total debe ser mayor que cero";
}

/**
 * ¿Hay algún procedimiento con plan (copago)? Copiado de TotalesFactura.js:
 *     const tienePlan = procedimientos.some(p => p.plan);
 */
export function tienePlanAplicado(procedimientos = []) {
    return procedimientos.some((p) => p.plan);
}

/**
 * Origen de un procedimiento: lo que permite distinguir tres líneas idénticas.
 *
 * ATENCIÓN a lo que NO hay, verificado contra los 10.371 pendientes reales de
 * la base (FASE 0):
 *   · NO existe ningún "número de orden" legible. `orden_uid` es un UUID que
 *     sólo tienen 4 filas de 10.371.
 *   · `idcita` falta en el 45% de los casos.
 *   · Ni siquiera cita+fecha bastan: hay seis TIROXINA del mismo paciente, la
 *     misma cita y el mismo día.
 *
 * Lo único que separa dos líneas SIEMPRE es `idprocedimiento_cita_paciente`.
 * Por eso el origen es «fecha · #id» y no el «Orden #4468» de la maqueta: ese
 * número no existe y no se inventa.
 */
export function origenProcedimiento(procedimiento) {
    const id = procedimiento?.idprocedimiento_cita_paciente ?? null;
    const cruda = procedimiento?.fecha_emision ?? null;
    // El día se lee del texto ISO, NO con `new Date(...).getDate()`: eso
    // interpreta en horario local y en Ecuador (UTC-5) un "2025-11-13T00:00Z"
    // se muestra como 12/11/2025 -- un día antes. En una pantalla de
    // facturación, una fecha corrida un día es un error, no un detalle.
    let fecha = "";
    const iso = String(cruda ?? "").slice(0, 10);
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
    if (m) fecha = `${m[3]}/${m[2]}/${m[1]}`;
    return { fecha, referencia: id != null ? `#${id}` : "" };
}

/**
 * Un importe legible, o "—" si no hay número.
 *
 * AUSENTE NO ES CERO. `Number(null)` y `Number("")` valen 0, así que la
 * comprobación ingenua pinta "$0.00" donde no hay precio -- y en una factura
 * un cero inventado es peor que un guion. Caí en esta trampa tres veces
 * (PVP, Total y el selector de pendientes) antes de extraerla aquí.
 */
export function montoLegible(valor) {
    if (valor === null || valor === undefined || valor === "") return "—";
    const n = Number(valor);
    return Number.isFinite(n) ? n.toFixed(2) : "—";
}

/** El importe de una línea: cantidad x precio, o null si no hay precio. */
export function importeLinea(procedimiento) {
    const crudo = procedimiento?.precio;
    if (crudo === null || crudo === undefined || crudo === "") return null;
    const precio = Number(crudo);
    if (!Number.isFinite(precio)) return null;
    const cantidad = Number(procedimiento?.cantidad) || 1;
    return precio * cantidad;
}
